import { useHistory } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { useState } from 'react';
import { Box, Typography } from '@mui/material';
import ExperienceWalletForm from 'components/forms/experienceWallet/ExperienceWalletForm';
import OnboardingSteps from 'components/OnboardingSteps';
import OnboardingNavigationButtons from 'components/OnboardingNavigationButtons';
import styles from 'styles/login.module.scss';
import layoutStyles from 'styles/layout.module.scss';
import { trackEvent } from 'lib/analytics';
import { MILITARY_EMPTY_EXPERIENCE } from 'constants/experienceDefaults';
import OnboardingContentWrapper from 'components/OnboardingContentWrapper';
import { useQueryClient } from '@tanstack/react-query';
import { BRANCH_OPTIONS } from 'constants/militaryOptions';

export default function OnboardingMilitaryExperience() {
  const intl = useIntl();
  const history = useHistory();
  const urlPrefix = intl.locale === 'en' ? '' : `/${intl.locale}`;
  const queryClient = useQueryClient();

  // Read cached military/civilian experience for pre-population on back navigation
  const cachedExp = queryClient.getQueryData(['/users/me/experience/']);
  const cachedMil = cachedExp?.jst?.military_experience?.[0];
  const cachedCivilian = cachedExp?.job_experience?.[0];
  const hasCachedMilitary = !!cachedMil;
  const hasCachedCivilian = !hasCachedMilitary && !!cachedCivilian;
  const initialExperience = cachedMil
    ? {
        ...MILITARY_EMPTY_EXPERIENCE,
        type: 'employment',
        // The backend military_experience schema only persists experience_title and
        // occupation_id (no moc_code/moc_title/soc) — rebuild the MilitaryMOCAutocomplete
        // value from those so returning to this page pre-fills the saved job title.
        field: cachedMil.occupation_id
          ? {
              moc_code: '',
              moc_title: cachedMil.experience_title || '',
              soc_title: '',
              soc_code: cachedMil.occupation_id,
            }
          : null,
        title: cachedMil.experience_title || '',
        skills: cachedMil.parsed_skills || [],
        start_date: cachedMil.start_date || null,
        end_date: cachedMil.end_date || null,
        comments: cachedMil.description || '',
        is_current: cachedMil.currently_enrolled ?? false,
        experienceCategory: 'military',
        branch:
          BRANCH_OPTIONS.find(
            (b) => b.apiValue === cachedMil.branch_of_service || b.backendName === cachedMil.branch_of_service
          )?.value || '',
        rank: cachedMil.rank || '',
      }
    : hasCachedCivilian
    ? {
        ...MILITARY_EMPTY_EXPERIENCE,
        type: cachedCivilian.type || '',
        field: cachedCivilian.fields?.[0] || null,
        title: cachedCivilian.title || '',
        skills: cachedCivilian.skills || [],
        start_date: cachedCivilian.start_date || null,
        end_date: cachedCivilian.end_date || null,
        employer_provider: cachedCivilian.employer_provider || '',
        comments: cachedCivilian.description || '',
        is_current: cachedCivilian.is_current ?? false,
        experienceCategory: 'civilian',
      }
    : MILITARY_EMPTY_EXPERIENCE;

  const [experienceCategory, setExperienceCategory] = useState(
    hasCachedMilitary ? 'military' : hasCachedCivilian ? 'civilian' : ''
  );
  const [hasSelectedCategory, setHasSelectedCategory] = useState(hasCachedMilitary || hasCachedCivilian);
  const [experiences, setExperiences] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleAppendItem = (item) => {
    setExperiences([...experiences, item]);
    trackEvent('ONBOARDING_MILITARY_EXPERIENCE_COMPLETE');
    history.push(`${urlPrefix}/onboarding/location?from=military-experience`);
  };

  // Reveal the form inline as soon as a category is picked
  const handleCategoryChange = (newCategory) => {
    setExperienceCategory(newCategory);
    setHasSelectedCategory(true);
  };

  const handleBack = () => {
    history.push({
      pathname: `${urlPrefix}/onboarding/eligibility`,
      state: { step: 2 },
    });
  };

  return (
    <Box sx={{ boxShadow: '0px 3px 12px rgba(0, 0, 0, 0.1)' }}>
      <div className={styles.loginFlow__form__onboarding}>
        <Box px={3}>
          <Typography align="center" variant="h1" gutterBottom sx={{ fontSize: 24, mt: 1 }}>
            {intl.formatMessage({
              id: hasSelectedCategory
                ? experienceCategory === 'military'
                  ? 'experience.form.addMilitaryExperience'
                  : 'experience.form.addWorkExperience'
                : 'experience.form.selectExperienceType',
            })}
          </Typography>
        </Box>
        <Box sx={{ maxWidth: 222, marginLeft: 'auto', marginRight: 'auto', mt: 4, mb: 1 }}>
          <OnboardingSteps activeStep={3} />
        </Box>
      </div>
      <hr className={layoutStyles.separatorBold} />
      <Box className={styles.loadingScreen__cardsContainer}>
        <OnboardingContentWrapper>
          <ExperienceWalletForm
            experience={initialExperience}
            experiences={experiences}
            formScope="military"
            appendItem={handleAppendItem}
            formId="onboarding-military-experience-form"
            onCategoryChange={handleCategoryChange}
            hasSelectedCategory={hasSelectedCategory}
            onSubmittingChange={setIsSubmitting}
          />
          <OnboardingNavigationButtons
            onBack={handleBack}
            loading={isSubmitting}
            onNext={() => {
              const formElement = document.getElementById('onboarding-military-experience-form');
              if (formElement) {
                formElement.requestSubmit();
              }
            }}
            nextDisabled={!experienceCategory || isSubmitting}
            backId="back-military-experience-button"
            nextId="next-military-experience-button"
          />
        </OnboardingContentWrapper>
      </Box>
    </Box>
  );
}
